import { Box, Button, Divider, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Dialog from 'components/dialog';
import Factory from 'components/google-maps/assets/Factory';
import PdfViewer from 'components/pdf-viewer';
import dayjs from 'dayjs';
import { ChevronDown, ChevronUp, Cylinder, Edit2, Lock } from 'lucide-react';
import Mui from 'materials';
import React, { useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import DeliveryBoardIcon from 'views/delivery-board/assets/delivery-board-icon';
import { DeliveryBoardItem } from 'views/delivery-board/types/delivery-board-model.type';
import { OrderShipmentStatus } from 'views/delivery-board/types/order-shipment-status.enum';
import CreateOrderDistribution from 'views/order-feed/features/order-distribution/components/create-order-distribution';
import { OrderAttachment } from 'views/order-feed/types/order-attachment.type';

const PREFIX = 'Card';

const classes = {
  button: `${PREFIX}-button`,
  open: `${PREFIX}-open`,
  default: `${PREFIX}-default`,
  label: `${PREFIX}-label`,
};

type StyleProps = {
  padding: number;
  marginBottom: number;
  marginTop: number;
};

const Root = styled('div', { label: 'Card' })<StyleProps>(({ theme, padding, marginBottom, marginTop }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(marginBottom),
  marginTop: theme.spacing(marginTop),
  background: theme.palette.common.white,
  [`&.${classes.default}`]: {
    background: theme.palette.common.white,
  },
  [`& .${classes.button}`]: {
    paddingLeft: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  ['.MuiTableCell-body']: {
    backgroundColor: `${theme.palette.lightBlue.o25} !important`,
  },
  [`& .MuiCollapse-wrapper`]: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
    },
  },
}));

const TitleBox = styled(Box)<StyleProps>(({ theme }) => ({
  padding: theme.spacing(2, 4, 2),
  [`&.${classes.open}`]: {
    borderBottom: `1px solid ${theme.palette.bluViu.o50}50`,
  },
}));

const IconOutline = styled(Box)(({}) => ({
  border: '1px solid #DDDDDD',
  borderRadius: 24,
  height: 22,
  width: 28,
  mr: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px, 8px, 4px, 8px',
  gap: 4,
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 800,
  color: theme.palette.darkBlue.main,
  textTransform: 'none',
}));

const TIME_TO_EDIT = 150;
const PAGE_NAME_ID = 'deliveryBoard.card';

type Props = {
  open?: boolean;
  count?: number;
  padding?: number;
  marginBottom?: number;
  marginTop?: number;
  elevation?: number;
  id?: string;
  sx?: SxProps<Theme> | undefined;
  fontWeight?: number;
  hiddenLabel?: boolean;
  hideBorder?: boolean;
  label?: string;
  data: DeliveryBoardItem;
};

const DeliveryCard: React.FC<Props> = ({
  label,
  open = false,
  count,
  padding = 4,
  sx,
  hideBorder = false,
  marginBottom = 2,
  marginTop = 0,
  fontWeight = 700,
  hiddenLabel,
  data,
  ...props
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(open);
  const history = useHistory();
  const intl = useIntl();

  const isDelayed = useMemo(() => {
    if (!data.initialEta) return false; // if we do not have an initial ETA, we assume the current ETA is the expected arrival time.
    return (
      (data.status === OrderShipmentStatus.ON_ROUTE || data.status === OrderShipmentStatus.LOADING) &&
      dayjs(data.currentEta).isSameOrAfter(dayjs(data.initialEta).add(24, 'hours'))
    ); // if the current ETA is after the initial ETA + 24 hours, the shipment is late.
  }, [data]);

  const dateTime = useMemo(() => {
    switch (data.status) {
      case OrderShipmentStatus.LOADING:
        return data.currentEta;

      case OrderShipmentStatus.ON_ROUTE:
        return data.currentEta;

      case OrderShipmentStatus.DELIVERED:
        return data.arrivalTime;
      default:
        return data.currentEta;
    }
  }, [data, isDelayed]);

  const color = useMemo(() => {
    return isDelayed ? `${theme.palette.error.main} !important` : `${theme.palette.darkBlue.main} !important`;
  }, [isDelayed]);

  const shouldDisableEdit = (eta: string | null) => {
    if (!eta || dayjs(eta).utc().diff(dayjs().utc(), 'minute') <= TIME_TO_EDIT) return true;
    return false;
  };

  const editable = useMemo(() => {
    return data.status !== OrderShipmentStatus.DELIVERED && !shouldDisableEdit(data.currentEta);
  }, [data]);

  const orderDocumentation = useMemo(() => {
    return {
      documentIds: data.documentIds,
      orderNumber: data.orderNumber,
      dispatchDate: data.dispatchDate,
      siteName: data.siteName,
    } as OrderAttachment;
  }, [data]);

  return (
    <Root
      padding={padding}
      marginTop={marginTop}
      marginBottom={marginBottom}
      className={isOpen ? classes.open : classes.default}
      sx={{ ...sx, border: hideBorder ? 'none' : `1px solid ${theme.palette.darkBlue.main}` }}>
      <Box>
        <TitleBox padding={padding} marginBottom={0} marginTop={0} fontWeight={fontWeight}>
          <Mui.ListItemButton
            disableRipple
            className={classes.button}
            onClick={() => setIsOpen((prev) => !prev)}
            sx={{
              padding: `${theme.spacing(1, 0, 1)}`,
            }}>
            <Box width={'100%'}>
              <Box display={'flex'} justifyContent={'space-between'} mb={2}>
                <Box display={'flex'} gap={2}>
                  <CardLabel>{data.siteName}</CardLabel>
                  <Typography color={theme.palette.darkBlue.o75}>{data.orderNumber}</Typography>
                </Box>
                <Box display={'flex'}>
                  <IconOutline sx={{ width: 42 }}>
                    <Cylinder size={14} stroke={theme.palette.primary.main} />
                    {editable ? (
                      <Edit2 size={12} color={theme.palette.primary.main} />
                    ) : (
                      <Lock size={12} color={theme.palette.primary.main} />
                    )}
                  </IconOutline>
                  {isOpen ? (
                    <ChevronUp size={24} color={theme.palette.primary.main} />
                  ) : (
                    <ChevronDown size={24} color={theme.palette.primary.main} />
                  )}
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box display={'flex'} gap={2} alignItems={'center'}>
                  <Factory />
                  <Typography color={'#415965'}>{data.plantName}</Typography>
                  <Divider sx={{ background: theme.palette.darkBlue.main }} orientation="vertical" variant="middle" flexItem />
                  <Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={20}>
                    <DeliveryBoardIcon fill="#809198" stroke="#809198" />
                  </Box>
                  <Typography color={'#012231'}>{data.vessel}</Typography>
                </Box>
                <Box display={'flex'} gap={2} alignItems={'center'}>
                  <Box color={color} fontSize={14} fontWeight={500}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.eta`} />:
                  </Box>
                  <CardLabel color={color} whiteSpace={'nowrap'}>
                    {dateTime ? <FormattedDate value={dateTime} day="2-digit" month="short" hour12={false} /> : 'N/A'}
                  </CardLabel>
                  <Divider sx={{ background: color }} orientation="vertical" variant="middle" flexItem />
                  <CardLabel color={color}>
                    {dateTime ? <FormattedTime value={dateTime} hour="2-digit" minute="2-digit" hour12={false} /> : 'N/A'}
                  </CardLabel>
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'flex-end'}>
                {isDelayed && (
                  <CardLabel color={color}>
                    (<FormattedMessage id={`${PAGE_NAME_ID}.delayed`} />)
                  </CardLabel>
                )}
              </Box>
            </Box>
          </Mui.ListItemButton>
        </TitleBox>
      </Box>
      <Mui.Collapse {...props} in={isOpen} sx={{ marginBottom: { xs: 2, md: 0 }, px: { xs: 4, md: 0 } }}>
        {editable ? (
          <Box textAlign={'center'} sx={{ backgroundColor: '#F0F3F9', borderRadius: 4, padding: 4 }}>
            <Box display={'flex'} gap={2} justifyContent={'center'} mb={2}>
              <Typography color={theme.palette.darkBlue.main}>
                <FormattedMessage id={`${PAGE_NAME_ID}.dispatchDate`} />
              </Typography>
              <CardLabel>
                <FormattedDate value={data.dispatchDate} day="2-digit" month="short" hour12={false} />
              </CardLabel>
            </Box>
            <Dialog
              title={
                intl.formatMessage({
                  id: `${PAGE_NAME_ID}.${editable ? `editDistribution` : `distribute`}`,
                }) + ` #${data.orderNumber}`
              }
              maxWidth={'lg'}
              button={
                <Button
                  size="small"
                  disabled={shouldDisableEdit(data.currentEta)}
                  onClick={() => {
                    if (shouldDisableEdit(data.currentEta)) {
                      // check on button click if too much time has passed since initial page visit, and trigger reload to prevent distribution navigation
                      window.location.reload();
                      return;
                    }
                  }}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.${editable ? `editDistribution` : `distribute`}`} />
                </Button>
              }>
              <CreateOrderDistribution distributionId={data.distributionId} />
            </Dialog>
          </Box>
        ) : null}
        <Box>
          <Box mt={5} display={'flex'} justifyContent={'space-between'}>
            <Typography color={'black'}>
              <FormattedMessage id={`${PAGE_NAME_ID}.product`} />
            </Typography>
            <Typography color={'black'}>
              <FormattedMessage id={`${PAGE_NAME_ID}.quantity`} />
            </Typography>
          </Box>
          <Divider sx={{ background: theme.palette.darkBlue.main }} orientation="horizontal" flexItem />
          {data.orderItems.map((item, key) => (
            <Box key={key}>
              <Box mt={5} display={'flex'} justifyContent={'space-between'}>
                <Typography color={'black'} textOverflow={'ellipsis'}>
                  {item.itemName}
                </Typography>
                <Typography color={'black'}>
                  <FormattedNumber
                    value={
                      data.status === OrderShipmentStatus.DELIVERED && !!item.deliveredQuantity
                        ? item.deliveredQuantity
                        : item.quantity
                    }
                  />
                  Kg
                </Typography>
              </Box>
              <Divider sx={{ background: '#C0C8CC', mt: 2 }} orientation="horizontal" flexItem />
            </Box>
          ))}
        </Box>
        <Box
          mt={5}
          display={'flex'}
          flexDirection={
            OrderShipmentStatus.DELIVERED && Number(data.documentIds?.length) > 0 && data.documentIds != undefined ? 'column' : 'row'
          }
          gap={2}>
          {OrderShipmentStatus.DELIVERED && Number(data.documentIds?.length) > 0 && data.documentIds != undefined && (
            <Dialog
              maxWidth="md"
              title={intl.formatMessage({ id: `${PAGE_NAME_ID}.deliveryDocuments` })}
              button={
                <Button variant="outlined" size="small" type="button">
                  <FormattedMessage id={`${PAGE_NAME_ID}.deliveryNotes`} />
                </Button>
              }>
              <PdfViewer orderAttachment={orderDocumentation} from={'delivery-board'} />
            </Dialog>
          )}
          <Button
            sx={{ width: '100%' }}
            size="small"
            onClick={() => {
              var url =
                data.status === OrderShipmentStatus.DELIVERED
                  ? `/order-feed/order-history/${data.siteErpId}/${data.globalSiteId}/${data.orderNumber}`
                  : `/order-feed/supply-plan/${data.siteErpId}/${data.globalSiteId}`;
              history.push(url, {
                // needed for order-history ViewOrder in order to render PDFViewer
                documentIds: data.documentIds,
                orderNumber: data.orderNumber,
                dispatchDate: data.dispatchDate,
                siteName: data.siteName,
              });
            }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.viewOrder`} />
          </Button>
        </Box>
      </Mui.Collapse>
    </Root>
  );
};

export default DeliveryCard;
